/**
 * SearchPage — 검색 · 카드 브라우즈 (PKA 화면정의서 SC-001).
 *
 * IA v0.1(rev) 진입 플로우 개정을 그대로 따른다:
 *   ① **카드 에디션(언어판)을 먼저 고른다** — 기본 한국판 (SC-009)
 *   ② 그 선택을 기준으로 카드를 표시한다
 * 에디션은 이 화면의 로컬 토글이 아니라 세트 상세·카드 상세까지 이어지는 전역 상태다
 * (`EditionContext`). 검색에서 일본판을 골랐는데 상세가 한국판 시세를 띄우면 안 된다.
 * 에디션은 그대로 BE 의 `language` 파라미터가 된다.
 *
 * 검색어가 없을 때는 빈 화면 대신 **세트 목록**을 깔아 UF-004 동선을 연다.
 *
 * 가져오지 않은 것: 게임 전환(Pokémon/Magic) — 포켓몬 전용 제품이라 탭 자체가 없다.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { track } from "../../lib/tracking";
import { useMoney } from "../../hooks/useMoney";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import { useTranslation } from "react-i18next";
import { Button } from "@astryxdesign/core/Button";
import { Divider } from "@astryxdesign/core/Divider";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Item } from "@astryxdesign/core/Item";
import { List } from "@astryxdesign/core/List";
import { Section } from "@astryxdesign/core/Section";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Token } from "@astryxdesign/core/Token";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import LazyImage from "../../components/primitives/LazyImage";
import { useEdition } from "../../components/edition/EditionContext";
import EditionSelector from "../../components/edition/EditionSelector";
import { cardImageUrl, type CardItem, type CardsetSummary } from "../../api/card";
import {
  useCardSearchQuery,
  useCardsetsQuery,
  flattenCards,
} from "../../hooks/useCardQueries";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useAuthStore } from "../../store/authStore";
import { maskPrice } from "../../lib/priceMasking";
import SetLogo from "../../components/card/SetLogo";
import { RECENT_SEARCHES_KEY } from "../../lib/localDataOwner";
import styles from "./SearchPage.module.css";

// 저장 키 정본은 localDataOwner — 계정 전환·탈퇴 시 지울 목록과 같은 곳에 둔다.
const RECENT_KEY = RECENT_SEARCHES_KEY;
const RECENT_MAX = 8;

/** 타이핑 중 매 글자마다 서버를 때리지 않도록. 한글 조합 중에도 과호출을 막는다. */
const DEBOUNCE_MS = 300;

/** 세트명 대조 최소 길이 — 한 글자로는 수십 종이 걸려 지름길이 아니라 소음이 된다. */
const SET_MATCH_MIN_QUERY = 2;
/** 세트는 카드 결과 **위에** 얹히는 지름길이라, 길어지면 정작 카드를 밀어낸다. */
const SET_RESULT_MAX = 5;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function saveRecent(next: string[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* storage 불가 환경 무시 */
  }
}

/**
 * 세트를 시리즈로 묶는다.
 *
 * ⚠ 카탈로그의 `seriesName` 은 현재 **전량 null** 이다(수집서버가 아직 안 채운다).
 * 그래서 "시리즈가 하나도 없으면 헤더 없이 평평하게" 를 정상 경로로 다룬다 —
 * null 을 "기타" 같은 가짜 그룹으로 만들면 전 세트가 그 아래 들어가 헤더가 의미를 잃는다.
 *
 * **이름순으로 정렬한다** (design 2026-08-18). 그전에는 서버가 준 순서 그대로라
 * 세트가 늘어날수록 찾을 방법이 눈으로 훑는 것뿐이었다. 시리즈끼리 한 번, 각 시리즈
 * 안의 세트끼리 한 번.
 * ⚠ localeCompare 에 "ko" 를 준다. 기본 비교(<)는 코드포인트 순이라 한글이 자모 순으로
 *   안 서고, 영문 세트명과 섞이면 대문자가 소문자보다 앞에 온다.
 */
function groupBySeries(sets: CardsetSummary[]): Array<[string | null, CardsetSummary[]]> {
  const map = new Map<string | null, CardsetSummary[]>();
  for (const set of sets) {
    const key = set.seriesName?.trim() ? set.seriesName : null;
    const list = map.get(key) ?? [];
    list.push(set);
    map.set(key, list);
  }
  const byName = (a: string, b: string) => a.localeCompare(b, "ko");
  for (const list of map.values()) list.sort((a, b) => byName(a.name, b.name));
  // 시리즈명 없는 그룹(현 카탈로그의 정상 경로)은 하나뿐이라 정렬 대상이 아니다.
  return [...map.entries()].sort(([a], [b]) => byName(a ?? "", b ?? ""));
}

export default function SearchPage() {
  const { t } = useTranslation("card");
  const money = useMoney();
  // 카드·세트 상세는 activity 로 뜬다 (뒤로가기가 검색으로 돌아와야 한다).
  const overlayNavigate = useOverlayNavigate();
  const { edition } = useEdition();
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [recent, setRecent] = useState<string[]>(loadRecent);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const isSearching = debouncedQuery.trim().length > 0;

  const search = useCardSearchQuery({ keyword: debouncedQuery, language: edition });
  const cardsets = useCardsetsQuery(edition);

  const results = flattenCards(search.data);

  /**
   * 검색 1회 — 질의어가 바뀌고 **첫 응답이 온 시점**에 한 번. 무한스크롤로 뒤 페이지가
   * 붙어도 재전송하지 않는다(ref 가드) — 첫 화면의 결과 수가 알고 싶은 값이다.
   *
   * ⚠ 검색어 **원문은 보내지 않는다** — 자유입력이라 PII 가 섞일 수 있다(GA 약관).
   *   길이와 결과 수만 보낸다. resultCount=0 이 몰리는 길이대가 카탈로그 구멍의 신호다.
   */
  const searchTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!q || !search.isSuccess) return;
    if (searchTrackedRef.current === q) return;
    searchTrackedRef.current = q;
    track("search_performed", {
      queryLength: q.length,
      resultCount: results.length,
      language: edition,
    });
  }, [debouncedQuery, search.isSuccess, results.length, edition]);
  /**
   * 세트명 검색 — **이미 받아 둔 목록**에서 고른다 (추가 요청 없음).
   *
   * BE `keyword` 는 카드명·카드번호만 본다(`CardQueryRepository.search`). 거기에 세트명을
   * 더하지 않은 이유: 세트 이름 하나로 카드 수백 장이 쏟아지면 찾던 것에서 오히려 멀어진다.
   * 세트는 세트 행으로 내놓고 눌러 들어가게 한다.
   *
   * 아래 브라우즈가 쓰는 에디션별 세트 목록이 이미 전량(ko 514종) 손에 있어, 이 대조는
   * 앱 안에서 끝난다 — 페이지 단위가 아니라 전량이므로 "스크롤할수록 결과가 는다"는
   * 앱측 필터링의 함정에도 걸리지 않는다.
   */
  const matchedSets = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (q.length < SET_MATCH_MIN_QUERY) return [];
    return (cardsets.data ?? [])
      .filter((set) => set.name.toLowerCase().includes(q) || set.code.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, "ko"));
  }, [debouncedQuery, cardsets.data]);

  const seriesGroups = useMemo(() => groupBySeries(cardsets.data ?? []), [cardsets.data]);
  /** 시리즈명이 하나도 없으면 헤더를 달지 않는다 (전부 null 인 현재 상태) */
  const hasSeriesLabels = seriesGroups.some(([series]) => series !== null);

  const sentinelRef = useInfiniteScroll({
    enabled: !!search.hasNextPage && !search.isFetchingNextPage,
    onIntersect: () => void search.fetchNextPage(),
  });

  const commitRecent = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    const next = [trimmed, ...recent.filter((r) => r !== trimmed)].slice(0, RECENT_MAX);
    setRecent(next);
    saveRecent(next);
  };

  const removeRecent = (term: string) => {
    const next = recent.filter((r) => r !== term);
    setRecent(next);
    saveRecent(next);
  };

  const pickRecent = (term: string) => {
    setQuery(term);
    setDebouncedQuery(term);
  };

  /**
   * 세트 행 — 검색 결과와 브라우즈가 **같은 꼴**이어야 한다. 화면 안에서 한 종류의
   * 행으로 읽혀야 하고, 한쪽만 고쳐져 갈라지는 것도 막는다.
   */
  const setRow = (set: CardsetSummary) => (
    <Item
      key={set.cardsetSerialNo}
      as="li"
      label={set.name}
      /* 세트 발행 언어가 아니라 **선택한 에디션**을 적는다 — 목록이 그 기준으로
         걸러져 있기 때문이다. 발매일은 없을 수 있다.
         수록 장수는 보유 진행률이 아니다 — 이 API 는 보유를 모른다. */
      description={[
        set.code.toUpperCase(),
        t(`edition.${edition}`),
        set.releasedOn?.slice(0, 4),
        t("search.setCardCount", { count: set.totalCardCount }),
      ]
        .filter(Boolean)
        .join(" · ")}
      labelLines={1}
      onClick={() => overlayNavigate(`/sets/${set.cardsetSerialNo}`)}
      startContent={
        /* 로고는 세트를 한눈에 알아보는 표지라 글자보다 먼저 눈에 들어와야 한다. */
        <div className={styles.setLogoBox}>
          <SetLogo logoUrl={cardImageUrl(set.logoImagePathway) ?? undefined} />
        </div>
      }
    />
  );

  /**
   * 목록 시세 — 표시할 값이 있을 때만. 비회원은 마스킹(기획확정안 §4.2).
   *
   * 현재 로컬/개발 카탈로그는 가격 테이블이 비어 있어 대부분 null 로 떨어진다. 이건
   * 오류가 아니라 정상 경로이므로 "시세 없음"으로 조용히 내려앉는다(§5.4).
   */
  const priceText = (card: CardItem): string | null => {
    const price = card.listPrice;
    if (price.display === "NONE" || price.amount === null) return null;
    const amount = isAuthenticated
      ? money.format(price.amount, price.currency ?? undefined)
      : maskPrice(price.amount);
    return price.display === "REFERENCE_CASE" ? t("search.referenceCase", { amount }) : amount;
  };

  /**
   * 카드 상세로 이동.
   *
   * ⚠ 넘기는 값은 **cardSerialNo**(수집서버 전역 ID)다 — BE 상세가
   * `GET /cards/{cardSerialNo}` 이고 스캔 매처가 돌려주는 키와도 같다.
   * 라우트 파라미터 이름은 아직 `:cardUid` 지만 그건 더미 시절 이름이다.
   *
   */
  const openCard = (card: CardItem) => {
    commitRecent(debouncedQuery);
    overlayNavigate(`/cards/${card.cardSerialNo}`);
  };

  return (
    <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
      <VStack gap={6}>
        {/* ① 진입 선행 단계 — 에디션을 먼저 정한다 (SC-009 · 기본 한국판).
            홈과 같은 블록을 쓴다. 이 선택이 이후 카드 표시와 시세 소스를 결정한다. */}
        <EditionSelector />

        {/* ② 선택한 에디션 기준으로 카드/세트 표시 */}
        <TextInput
          label={t("search.label")}
          isLabelHidden
          placeholder={t("search.placeholder")}
          value={query}
          onChange={(value) => setQuery(value)}
          onBlur={() => commitRecent(query)}
          size="lg"
        />

        {!isSearching && recent.length > 0 && (
          <VStack gap={3}>
            <HStack justify="between" align="center">
              <Heading level={3}>{t("search.recent")}</Heading>
              {/* 전체 지우기 (SC-001 · FN-006, design 2026-08-28) — 개별 × 는 있었지만
                  한 번에 비우는 길이 없었다. */}
              <Button
                variant="ghost"
                size="sm"
                data-compact=""
                label={t("search.clearRecent")}
                clickAction={() => { setRecent([]); saveRecent([]); }}
              />
            </HStack>
            <HStack gap={1} wrap="wrap">
              {recent.map((term) => (
                <Token
                  key={term}
                  label={term}
                  onClick={() => pickRecent(term)}
                  onRemove={() => removeRecent(term)}
                />
              ))}
            </HStack>
          </VStack>
        )}

        {/* 최근 검색어 — 아래로 시리즈 브라우즈가 이어지므로 구분선으로 끊는다
            (design 2026-08-20). 둘 다 "검색 전에 보는 것" 이라 제목만으로는 한 덩어리처럼
            읽혔다. 시리즈가 없으면(=이을 것이 없으면) 선도 긋지 않는다. */}
        {!isSearching && recent.length > 0 && seriesGroups.length > 0 && <Divider />}

        {/* UF-004 진입로: 검색어가 없을 때는 세트 브라우즈를 깔아준다 */}
        {!isSearching && (
          <VStack gap={5}>
            {cardsets.isPending && <SetGridSkeleton />}

            {cardsets.isError && (
              <EmptyState
                title={t("search.setsLoadFailed")}
                description={t("search.loadFailedBody")}
                actions={
                  <Button
                    variant="secondary"
                    label={t("search.retry")}
                    clickAction={() => void cardsets.refetch()}
                  />
                }
              />
            )}

            {cardsets.isSuccess && cardsets.data.length === 0 && (
              <EmptyState title={t("search.noSets")} description={t("search.noSetsBody")} />
            )}

            {cardsets.isSuccess && seriesGroups.map(([series, sets]) => (
              <VStack key={series ?? "_"} gap={3}>
                {/* 시리즈명이 없는 카탈로그에서는 헤더 대신 세트 수만 알린다 */}
                <HStack gap={2} justify="between" align="center">
                  <Text type="body" weight="semibold" as="p">
                    {hasSeriesLabels && series ? series : t("search.browseTitle")}
                  </Text>
                  <Text type="supporting" color="secondary">
                    {t("search.setCount", { count: sets.length })}
                  </Text>
                </HStack>
                {/* 세트도 행으로 둔다 (design 2026-08-18). 타일 격자였는데, 세트는
                    밀집 데이터라 Card 로 감싸지 않는 게 맞다 (DESIGNER.md §2) — 아래
                    검색 결과와 같은 꼴이라 화면 안에서 한 종류의 행으로 읽힌다.
                    이름순으로 훑을 때도 세로 한 줄이 격자보다 빠르다. */}
                <List hasDividers>{sets.map(setRow)}</List>
              </VStack>
            ))}
          </VStack>
        )}

        {isSearching && (
          <VStack gap={5}>
            {/* 세트명으로 걸린 것 — 카드 결과 **위**에 둔다. 세트 이름을 친 사람이
                찾는 것은 그 세트지 거기 실린 카드 190장이 아니다. */}
            {matchedSets.length > 0 && (
              <VStack gap={3}>
                <HStack gap={2} justify="between" align="center">
                  <Text type="body" weight="semibold" as="p">
                    {t("search.setResultTitle")}
                  </Text>
                  <Text type="supporting" color="secondary">
                    {t("search.setCount", { count: matchedSets.length })}
                  </Text>
                </HStack>
                <List hasDividers>{matchedSets.slice(0, SET_RESULT_MAX).map(setRow)}</List>
                {/* 잘라낸 사실을 밝힌다 — 위 '세트 N종' 과 보이는 줄 수가 다르다. */}
                {matchedSets.length > SET_RESULT_MAX && (
                  <Text type="supporting" color="secondary">
                    {t("search.setMore", { count: matchedSets.length - SET_RESULT_MAX })}
                  </Text>
                )}
              </VStack>
            )}

          <VStack gap={3}>
            {search.isError ? (
              <EmptyState
                title={t("search.loadFailed")}
                description={t("search.loadFailedBody")}
                actions={
                  <Button
                    variant="secondary"
                    label={t("search.retry")}
                    clickAction={() => void search.refetch()}
                  />
                }
              />
            ) : search.isPending ? (
              <ResultListSkeleton />
            ) : results.length === 0 ? (
              /* 세트가 걸렸으면 빈 상태를 띄우지 않는다 — 바로 위에 결과가 있는데
                 "검색 결과가 없어요" 라고 적으면 거짓말이다. */
              matchedSets.length > 0 ? null : (
                <EmptyState title={t("search.empty")} description={t("search.emptyBody")} />
              )
            ) : (
              <>
                {/* BE 가 전체 건수를 주지 않는다(COUNT 회피) — 지금까지 받은 만큼만
                    세고, 더 있으면 그 사실을 따로 알린다. 총계로 단정하지 않는다. */}
                <Text type="body" weight="semibold" as="p">
                  {search.hasNextPage
                    ? t("search.resultCountMore", { count: results.length })
                    : t("search.resultCount", { count: results.length })}
                </Text>

                {/* 밀집 데이터는 Card 로 감싸지 않고 행으로 둔다 (DESIGNER.md §2) */}
                <List hasDividers>
                  {results.map((card) => {
                    const price = priceText(card);
                    const imageUrl = cardImageUrl(card.imagePathway);
                    return (
                      <Item
                        key={card.cardSerialNo}
                        as="li"
                        label={card.name}
                        description={[card.number, card.rarity, card.variantLabel, card.cardsetName]
                          .filter(Boolean)
                          .join(" · ")}
                        labelLines={1}
                        onClick={() => openCard(card)}
                        startContent={
                          <div className={styles.thumbBox}>
                            {imageUrl ? (
                              <LazyImage className={styles.artwork} src={imageUrl} />
                            ) : (
                              <span className={styles.artworkEmpty} aria-hidden="true" />
                            )}
                          </div>
                        }
                        endContent={
                          <Text
                            weight={price ? "bold" : "normal"}
                            color={price ? "primary" : "secondary"}
                            type={price ? "body" : "supporting"}
                          >
                            {price ?? t("search.noPrice")}
                          </Text>
                        }
                      />
                    );
                  })}
                </List>

                {/* IntersectionObserver 가 동작하지 않는 환경(테스트·구형 웹뷰)에서도
                    다음 페이지로 갈 수 있게 버튼을 함께 둔다. */}
                {search.hasNextPage && (
                  <div ref={sentinelRef}>
                    <Button
                      variant="secondary"
                      width="fill"
                      isDisabled={search.isFetchingNextPage}
                      label={
                        search.isFetchingNextPage
                          ? t("search.loadingMore")
                          : t("search.loadMore")
                      }
                      clickAction={() => void search.fetchNextPage()}
                    />
                  </div>
                )}
                {!search.hasNextPage && results.length >= 20 && (
                  <Text type="supporting" color="secondary" justify="center">
                    {t("search.listEnd")}
                  </Text>
                )}
              </>
            )}
          </VStack>
          </VStack>
        )}
      </VStack>
    </Section>
  );
}

function ResultListSkeleton() {
  return (
    <VStack gap={3} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} height={56} />
      ))}
    </VStack>
  );
}

/** 세트 목록이 행이 되면서 스켈레톤도 행 높이로 (design 2026-08-18). */
function SetGridSkeleton() {
  return (
    <VStack gap={2} aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} height={56} />
      ))}
    </VStack>
  );
}
